import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { 
  MessageCircle, CheckCircle, Phone, Mail, MapPin, Star, 
  Truck, Package, Shield, Camera, Sofa, Wrench, Trash2, 
  ShoppingBag, GraduationCap, Warehouse, ArrowRight, Quote,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { generateWhatsAppLink, trackWhatsAppClick } from '@/utils/whatsappTemplates';
import { siteContent } from '@/data/siteContent';
import { getPartnerBySlug, partnerServices } from '@/data/partnerConfig';
import { CONTACT_INFO, TRUST_BADGES } from '@/data/mockData';
import api from '@/services/api';

const { global } = siteContent;

// Icon mapping
const iconMap = {
  Truck, Package, Shield, Camera, Sofa, Wrench, Trash2, 
  ShoppingBag, GraduationCap, Warehouse
};

// Service definitions for dynamic partners
const dynamicServices = {
  student_moves: { title: 'Student Moves', description: 'Move in/out of res, apartments, and student digs', icon: 'GraduationCap' },
  res_deliveries: { title: 'Res Deliveries', description: 'Deliver items to campus residences', icon: 'Package' },
  storage_runs: { title: 'Storage Runs', description: 'Move items to and from storage units', icon: 'Warehouse' },
  marketplace: { title: 'Marketplace Pickups', description: 'Collect and deliver marketplace purchases', icon: 'ShoppingBag' },
  furniture_delivery: { title: 'Furniture Delivery', description: 'Safe delivery and placement of furniture', icon: 'Sofa' },
  same_day: { title: 'Same-Day Delivery', description: 'Urgent deliveries within hours', icon: 'Truck' },
  scheduled: { title: 'Scheduled Delivery', description: 'Book ahead for specific time slots', icon: 'Shield' },
  returns: { title: 'Returns & Exchanges', description: 'Handle product returns efficiently', icon: 'Package' },
  office_moves: { title: 'Office Moves', description: 'Professional office and equipment relocation', icon: 'Warehouse' },
  event_logistics: { title: 'Event Logistics', description: 'Equipment delivery for events', icon: 'Truck' }
};

const PartnerPage = () => {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPartner = async () => {
      setIsLoading(true);
      try {
        // Try to fetch from API first (dynamic partners from admin settings)
        const response = await api.get(`/api/admin/public/partners/${slug}`);
        if (response.data.success && response.data.partner) {
          setPartner(response.data.partner);
          setIsLoading(false);
          return;
        }
      } catch (error) {
        // API failed or partner not found in dynamic settings

      }

      // Fall back to static partner config
      const staticPartner = getPartnerBySlug(slug);
      if (staticPartner) {
        setPartner(staticPartner);
      } else {
        setNotFound(true);
      }
      setIsLoading(false);
    };

    fetchPartner();
  }, [slug]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
      </div>
    );
  }

  // If partner not found, redirect to 404
  if (notFound || !partner) {
    return <Navigate to="/404" replace />;
  }

  // Get services - support both static and dynamic formats
  const getServiceData = (serviceKey) => {
    // First check static partnerServices
    if (partnerServices[serviceKey]) {
      return partnerServices[serviceKey];
    }
    // Then check dynamic services
    if (dynamicServices[serviceKey]) {
      return dynamicServices[serviceKey];
    }
    // Fallback
    return { title: serviceKey, description: '', icon: 'Package' };
  };

  const handleWhatsAppClick = (variables = {}) => {
    trackWhatsAppClick({ templateName: 'partner', page: `partner-${slug}` });
    const discountCode = partner.discountCode || partner.discount_code;
    const message = discountCode 
      ? `Hi! I'm a ${partner.name} customer. Code: ${discountCode}`
      : `Hi! I'm interested in delivery via ${partner.name} partnership.`;
    const link = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(link, '_blank');
  };

  // Support both static (discountCode) and dynamic (discount_code) formats
  const discountCode = partner.discountCode || partner.discount_code;
  const primaryColor = partner.primaryColor || partner.primary_color || '#001A47';
  const discount = partner.discount || (partner.discount_percent ? `${partner.discount_percent}% Discount` : null);

  return (
    <div className="min-h-screen" data-testid="partner-page">
      {/* Hero */}
      <section 
        className="relative py-16 md:py-24 text-white overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${primaryColor} 0%, #001A47 100%)`
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#fb6402] rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Partner Badge */}
            <Badge className="bg-white/20 text-white mb-4 px-4 py-1">
              {partner.tagline}
            </Badge>

            {/* Co-branding */}
            <div className="flex items-center justify-center gap-4 mb-6">
              {partner.logo ? (
                <img src={partner.logo} alt={partner.name} className="h-12" />
              ) : (
                <span className="text-2xl font-bold">{partner.name}</span>
              )}
              <span className="text-2xl text-white/50">×</span>
              <span className="text-2xl font-bold">Rocket Van Express</span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              {partner.description.split('.')[0]}
            </h1>

            {discount && (
              <div className="inline-flex items-center gap-2 bg-[#fb6402] text-white px-6 py-3 rounded-full mb-8">
                <Star className="h-5 w-5" />
                <span className="font-bold">{discount}</span>
                {discountCode && (
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                    Code: {discountCode}
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#25D366] hover:bg-[#22c55e] text-white px-8"
                onClick={handleWhatsAppClick}
                data-testid="partner-whatsapp-btn"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Get Partner Quote
              </Button>
              <Link to={`/quote?partner=${slug}&code=${discountCode || ''}`}>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-[#001A47] px-8"
                >
                  Online Quote Form
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#001A47] mb-4">
              Services for {partner.name} Customers
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              As a {partner.name} partner, you get access to these specialized services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {partner.services && partner.services.map((serviceKey) => {
              const service = getServiceData(serviceKey);
              const Icon = iconMap[service.icon] || Package;
              
              return (
                <Card key={serviceKey} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${primaryColor}15` }}
                    >
                      <Icon className="h-6 w-6" style={{ color: primaryColor }} />
                    </div>
                    <h3 className="font-semibold text-[#001A47] mb-2">{service.title}</h3>
                    <p className="text-sm text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#001A47] mb-4">Service Areas</h2>
              <p className="text-gray-600">
                We serve {partner.name} customers in these areas
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              {partner.areas && partner.areas.map((area, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full"
                >
                  <MapPin className="h-4 w-4 text-[#039fff]" />
                  <span className="text-[#001A47] font-medium">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {partner.testimonial && (
        <section className="py-16 bg-[#001A47] text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Quote className="h-12 w-12 text-[#039fff] mx-auto mb-6 opacity-50" />
              <blockquote className="text-xl md:text-2xl mb-6 italic">
                "{partner.testimonial.quote}"
              </blockquote>
              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#039fff] flex items-center justify-center">
                  <span className="text-lg font-bold">
                    {partner.testimonial.author.charAt(0)}
                  </span>
                </div>
                <div className="text-left">
                  <p className="font-semibold">{partner.testimonial.author}</p>
                  <p className="text-gray-400 text-sm">{partner.testimonial.role}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Trust Badges */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {TRUST_BADGES.slice(0, 4).map((badge, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <CheckCircle className="h-8 w-8 text-[#039fff] mb-2" />
                <p className="text-sm font-medium text-gray-700">{badge.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto overflow-hidden">
            <div 
              className="p-8 md:p-12 text-white text-center"
              style={{ 
                background: `linear-gradient(135deg, ${primaryColor} 0%, #001A47 100%)`
              }}
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-gray-200 mb-6 max-w-lg mx-auto">
                {discountCode 
                  ? `Use code ${discountCode} when booking to claim your ${discount}!`
                  : `Contact us now to get your ${partner.name} partner rate.`
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8"
                  onClick={handleWhatsAppClick}
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp Us Now
                </Button>
                <a href={`tel:${global.phone}`}>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-white text-white hover:bg-white hover:text-[#001A47] px-8"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    {global.phone}
                  </Button>
                </a>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Partner Contact (if provided) */}
      {partner.contact && (
        <section className="py-8 border-t">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-sm text-gray-600">
              <span>Questions about this partnership?</span>
              {partner.contact.email && (
                <a href={`mailto:${partner.contact.email}`} className="flex items-center gap-2 hover:text-[#039fff]">
                  <Mail className="h-4 w-4" />
                  {partner.contact.email}
                </a>
              )}
              {partner.contact.phone && (
                <a href={`tel:${partner.contact.phone}`} className="flex items-center gap-2 hover:text-[#039fff]">
                  <Phone className="h-4 w-4" />
                  {partner.contact.phone}
                </a>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PartnerPage;
